import { ltr } from '../format';

/**
 * The warm-up offered before a level, its simulated prompt, and the words
 * baked into its diagrams (components/warmup/*).
 *
 * See web/src/lib/strings/index.ts for how this file is shaped and why, and
 * docs/i18n.md for the Arabic glossary every value here must follow. Neither
 * object may be `as const`: `ar` is typed against `en`, and literal types
 * would make it have to repeat the English words.
 *
 * ## The three groups
 *
 * `rep` is the simulated prompt's own chrome, and it is deliberately thin:
 * everything inside the screen — the command, what it printed, the note and
 * the nudge — is authored per card and arrives from the mission, not from
 * here (docs/i18n.md §6.1). The prompt string itself, `engineer@lattice:… $`,
 * is not in this file at all. It is the guest's own prompt, character for
 * character, and a translated one would be a lie about what is on screen.
 *
 * `fig` is the harder half: the labels drawn into the diagrams beside a card.
 *
 * ## Which words in a drawing are words
 *
 * docs/i18n.md §2.1. A figure is an author's drawing with prose on it, so the
 * prose is translated — `working tree`, `edited`, `staged`, `committed`, `one
 * change, three places`. What is *not* translated, and is therefore not in
 * this file, is everything the learner types or reads printed: `HEAD`,
 * `main`, `feature`, `git add`, `git commit`, `stash@{0}`, `--soft`, every
 * filename, every hash, every test name, `TypeError:` and the stack frames
 * under it, `good` and `bad` (which are `git bisect good` and `git bisect
 * bad`), and `nothing to commit` (which is the first half of a line
 * `git status` prints). Those stay in the components as literals, in Latin,
 * left to right, which is how the learner will meet them a minute later.
 *
 * Where an Arabic label has to name one of those tokens anyway — `commit`,
 * `reset`, `reflog`, `hash`, `>`, `-r` — the token is wrapped in `ltr()`.
 * A drawing is set left to right in both languages (styles/locale.css, and
 * §7), so a mixed label inside it is a right-to-left phrase inside a
 * left-to-right box, and without the isolate `>` renders mirrored as `<` and
 * a flag like `-r` loses its dash to the word beside it. `Label` in
 * figures/frame.tsx sets each of these in the reader's own direction; the
 * isolate is what keeps the token inside it intact.
 */

/** `07`. Both languages set Western digits, so the padding is shared. */
const pad = (level: number): string => String(level).padStart(2, '0');

/**
 * `3 اختبارات`. Arabic agrees a counted noun with its number across five
 * categories where English has two, and four of them are reachable by a test
 * count. Same mechanism as `plural` in play.ts, written out here because this
 * file counts exactly one kind of thing.
 */
function tests(n: number): string {
  if (n === 1) return 'اختبار واحد';
  if (n === 2) return 'اختباران';
  const rest = n % 100;
  if (rest >= 3 && rest <= 10) return `${n} اختبارات`;
  return `${n} اختبارًا`;
}

/* ── English ──────────────────────────────────────────────────────────────── */

export const en = {
  /* --- The overlay (Warmup.tsx) ------------------------------------------ */

  /** The eyebrow over the heading. The level number is padded, as on the map. */
  idLabel: (level: number) => `Warm-up · Level ${pad(level)}`,
  /** The heading, when a tutorial did not write one of its own. */
  title: 'Warm-up',
  /** The live region. The rail across the top says the same thing to the eye. */
  step: (i: number, total: number, tool: string) => `Card ${i} of ${total}: ${tool}`,
  skip: 'Skip',
  skipTitle: 'Close the warm-up (Esc)',
  back: 'Back',
  next: 'Next',
  start: 'Start the level',

  /* --- The rep (TryPrompt.tsx) ------------------------------------------- */

  rep: {
    head: 'Guided example',
    scope: 'This card checks a guided example and shows prepared output. It does not run commands or change your level files.',
    exampleOnly: 'For other commands, use the level’s terminal. This card checks the following example:',
    show: 'Show me',
    /** Only when the card's author did not write a nudge of their own. */
    nudge: 'Try the example above, or choose Show me.',
    /** An empty `output:` is a real answer. Named, so it does not read as broken. */
    silent: '(no output)',
    inputLabel: 'Type the command here, then press Enter',
  },

  /* --- The drawings (figures/*) ------------------------------------------
     One entry per prose label, shared where the same words appear in more
     than one drawing — `working tree` is in three of them. */

  fig: {
    /* three-trees, reset-modes, stash */
    workingTree: 'working tree',
    index: 'index',
    edited: 'edited',
    staged: 'staged',
    committed: 'committed',
    oneChangeThreePlaces: 'one change, three places',
    resetReach: 'how far back the reset reaches',

    /* revert */
    bug: 'bug',
    undoes: 'undoes',
    historyGrows: 'history grows; nothing is rewritten',

    /* filesystem, redirect, search */
    fromWhereYouStand: 'from where you stand',
    screen: 'screen',
    redirectSends: '> sends it to a file instead',
    recursiveWalk: '-r walks every file below here',

    /* commit-chain, branch-pointer, merge, rebase, cherry-pick, squash */
    pointsAtParent: 'each commit points at its parent',
    nameMoves: 'the name moves; the commits do not',
    twoParents: 'one commit, two parents',
    replayed: 'replayed one at a time onto the new base',
    sameChangeNewCommit: 'the same change, a new commit',
    threeCommits: 'three commits',
    oneCommit: 'one commit',
    workSurvives: 'the work survives; the commits do not',

    /* reflog, bisect */
    unreachable: 'unreachable',
    reflogHasHash: 'the reflog still has its hash',
    midpoint: 'midpoint',
    halves: 'each test halves what is left',

    /* remote, conflict */
    local: 'local',
    resolved: 'resolved',
    pickOne: 'you pick one, then delete the markers',

    /* test-run, stack-trace */
    testCount: (n: number) => `${n} test${n === 1 ? '' : 's'}`,
    passedCount: (n: number) => `${n} passed`,
    failedCount: (n: number) => `${n} failed`,
    yourCode: 'your code',
    firstFrame: 'the first frame in your own files',
  },
};

/* ── Arabic ───────────────────────────────────────────────────────────────── */

export const ar: typeof en = {
  idLabel: (level: number) => `تمرين سريع · المستوى ${pad(level)}`,
  title: 'تمرين سريع',
  // The tool is a command — `cat FILE`, `echo … > FILE` — so it is isolated:
  // this sentence is read aloud by a screen reader and written into a live
  // region, and bidi would otherwise reorder anything with a slash or a dot.
  step: (i: number, total: number, tool: string) => `البطاقة ${i} من ${total}: ${ltr(tool)}`,
  skip: 'تخطّي',
  skipTitle: 'أغلق التمرين السريع (Esc)',
  back: 'رجوع',
  next: 'التالي',
  start: 'ابدأ المستوى',

  rep: {
    head: 'مثال موجّه',
    scope: 'تتحقق هذه البطاقة من مثال موجّه وتعرض مخرجات معدّة مسبقًا. لا تشغّل أوامر ولا تغيّر ملفات المستوى.',
    exampleOnly: 'لتجربة أوامر أخرى، استخدم طرفية المستوى. تتحقق هذه البطاقة من المثال التالي:',
    show: 'أرني',
    nudge: 'جرّب المثال أعلاه، أو اختر «أرني».',
    silent: '(لا مخرجات)',
    inputLabel: 'اكتب الأمر هنا، ثم اضغط Enter',
  },

  fig: {
    workingTree: ltr('working tree'),
    // These are the labels Git documentation and engineering teams use; keep
    // them LTR even though the figure sits inside an Arabic page.
    index: ltr('staging area'),
    // One pattern for the three states of a change, so they read as a series.
    edited: 'معدَّل',
    staged: ltr('staged'),
    committed: ltr('committed'),
    oneChangeThreePlaces: 'تغيير واحد، ثلاثة مواضع',
    resetReach: `إلى أي مدى يرجع ${ltr('reset')}`,

    bug: 'خلل',
    undoes: 'يُلغي',
    historyGrows: `يُضاف ${ltr('commit')} جديد دون تغيير السجل السابق`,

    fromWhereYouStand: 'بدءًا من المجلد الحالي',
    screen: 'الشاشة',
    // The `>` is isolated, not merely left in Latin: it is a mirrored
    // character, and inside a right-to-left run a browser draws it as `<`.
    redirectSends: `${ltr('>')} يرسل المخرجات إلى ملف بدل الشاشة`,
    recursiveWalk: `${ltr('-r')} يمرّ بكل ملف تحت هذا المجلد`,

    pointsAtParent: `يشير ${ltr('commit')} إلى أصله: ${ltr('parent commit')}`,
    nameMoves: `يتغيّر موضع الفرع، وتبقى الـ ${ltr('commits')} كما هي`,
    twoParents: `${ltr('commit')} واحد، واثنان من ${ltr('parents')}`,
    replayed: 'تُطبّق التغييرات بالتتابع من نقطة بداية جديدة',
    sameChangeNewCommit: `التغيير نفسه، ${ltr('commit')} جديد`,
    threeCommits: `ثلاثة ${ltr('commits')}`,
    oneCommit: `${ltr('commit')} واحد`,
    workSurvives: `تبقى التغييرات ضمن ${ltr('commit')} جديد`,

    // The same words the live graph puts on an unreachable commit
    // (play.ts, `graph.unreachable`), because it is the same drawing.
    unreachable: 'غير قابل للوصول',
    reflogHasHash: `ما زال ${ltr('hash')} موجودًا في ${ltr('reflog')}`,
    midpoint: 'المنتصف',
    halves: 'كل اختبار يقسم ما تبقّى نصفين',

    local: 'المستودع المحلي',
    resolved: 'بعد الحلّ',
    pickOne: 'تختار واحدًا ثم تحذف العلامات',

    testCount: (n: number) => tests(n),
    // A verb and a number, which needs no agreement at any count.
    passedCount: (n: number) => `نجح ${n}`,
    failedCount: (n: number) => `فشل ${n}`,
    yourCode: 'شيفرتك',
    firstFrame: 'أول استدعاء يشير إلى ملف من مشروعك',
  },
};

export const de: typeof en = {
  idLabel: (level) => `Aufwärmen · Level ${pad(level)}`,
  title: 'Aufwärmen',
  step: (i, total, tool) => `Karte ${i} von ${total}: ${tool}`,
  skip: 'Überspringen',
  skipTitle: 'Aufwärmen schließen (Esc)',
  back: 'Zurück',
  next: 'Weiter',
  start: 'Level starten',
  rep: {
    head: 'Geführtes Beispiel',
    scope: 'Diese Karte prüft ein geführtes Beispiel und zeigt vorbereitete Ausgaben. Sie führt keine Befehle aus und ändert keine Leveldateien.',
    exampleOnly: 'Verwende das Terminal im Level für andere Befehle. Diese Karte prüft folgendes Beispiel:',
    show: 'Zeig es mir',
    nudge: 'Versuche das Beispiel oben oder wähle „Zeig es mir“.',
    silent: '(keine Ausgabe)',
    inputLabel: 'Tipp den Befehl hier ein und drück dann Enter',
  },
  fig: {
    workingTree: 'Arbeitsverzeichnis',
    index: 'Index',
    edited: 'bearbeitet',
    staged: 'gestagt',
    committed: 'committet',
    oneChangeThreePlaces: 'eine Änderung, drei Orte',
    resetReach: 'wie weit der Reset zurückreicht',
    bug: 'Bug',
    undoes: 'macht rückgängig',
    historyGrows: 'die Historie wächst; nichts wird umgeschrieben',
    fromWhereYouStand: 'von dort, wo du stehst',
    screen: 'Bildschirm',
    redirectSends: '> schickt es stattdessen in eine Datei',
    recursiveWalk: '-r geht jede Datei hier drunter durch',
    pointsAtParent: 'jeder Commit zeigt auf seinen Vorgänger',
    nameMoves: 'der Name bewegt sich; die Commits nicht',
    twoParents: 'ein Commit, zwei Vorgänger',
    replayed: 'einer nach dem anderen auf die neue Basis gespielt',
    sameChangeNewCommit: 'dieselbe Änderung, ein neuer Commit',
    threeCommits: 'drei Commits',
    oneCommit: 'ein Commit',
    workSurvives: 'die Arbeit bleibt; die Commits nicht',
    unreachable: 'unerreichbar',
    reflogHasHash: 'das Reflog hat seinen Hash noch',
    midpoint: 'Mitte',
    halves: 'jeder Test halbiert, was übrig ist',
    local: 'lokal',
    resolved: 'aufgelöst',
    pickOne: 'du wählst eine Seite und löschst dann die Markierungen',
    testCount: (n) => `${n} ${n === 1 ? 'Test' : 'Tests'}`,
    passedCount: (n) => `${n} bestanden`,
    failedCount: (n) => `${n} fehlgeschlagen`,
    yourCode: 'dein Code',
    firstFrame: 'der erste Frame in deinen eigenen Dateien',
  },
};

export const ja: typeof en = {
  idLabel: (level: number) => `ウォームアップ · レベル${pad(level)}`,
  title: 'ウォームアップ',
  step: (i: number, total: number, tool: string) => `カード ${i}/${total}: ${tool}`,
  skip: 'スキップ',
  skipTitle: 'ウォームアップを閉じる(Esc)',
  back: '戻る',
  next: '次へ',
  start: 'レベルを開始',
  rep: {
    head: 'ガイド付きの例',
    scope: 'このカードは例に沿った入力を確認し、用意された出力を表示します。コマンドの実行やレベルのファイル変更はしません。',
    exampleOnly: 'ほかのコマンドはレベルのターミナルで試してください。このカードで確認する例は次のとおりです。',
    show: '答えを見る',
    nudge: '上の例を試すか、「答えを見る」を選んでください。',
    silent: '(出力なし)',
    inputLabel: 'ここにコマンドを入力して、Enterを押してください',
  },
  fig: {
    workingTree: '作業ツリー',
    index: 'インデックス',
    edited: '編集済み',
    staged: 'ステージ済み',
    committed: 'コミット済み',
    oneChangeThreePlaces: '1つの変更、3つの場所',
    resetReach: 'リセットがさかのぼる範囲',
    bug: 'バグ',
    undoes: '取り消す',
    historyGrows: '履歴は増えるだけで、書き換えられません',
    fromWhereYouStand: '今いる場所から',
    screen: '画面',
    redirectSends: '> は代わりにファイルへ送る',
    recursiveWalk: '-r はここより下のすべてのファイルをたどる',
    pointsAtParent: '各コミットは親コミットを指す',
    nameMoves: '名前は動くが、コミットは動かない',
    twoParents: '1つのコミット、2つの親',
    replayed: '新しいベースの上に1つずつ再適用',
    sameChangeNewCommit: '同じ変更、新しいコミット',
    threeCommits: '3つのコミット',
    oneCommit: '1つのコミット',
    workSurvives: '作業は残り、コミットは残らない',
    unreachable: '到達不能',
    reflogHasHash: 'reflogにはまだハッシュが残っている',
    midpoint: '中間点',
    halves: 'テストのたびに、残りが半分になる',
    local: 'ローカル',
    resolved: '解消後',
    pickOne: '片方を選んでから、マーカーを削除する',
    testCount: (n: number) => `${n}件のテスト`,
    passedCount: (n: number) => `${n}件合格`,
    failedCount: (n: number) => `${n}件失敗`,
    yourCode: 'あなたのコード',
    firstFrame: 'あなた自身のファイルにある最初のフレーム',
  },
};
